import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  createOrganisationUser,
  systemManagerAddedMessage,
} from '@/api/resources/organisation-users'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { UserForm } from '@/components/users/user-form'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/users/$organisationId/system-managers/new')({
  head: () => ({ meta: [{ title: 'New System Manager - MyJunea' }] }),
  component: NewUserRoute,
})

function NewUserRoute(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/users/$organisationId/system-managers',
        params: { organisationId },
        resetScroll: false,
      }),
  )
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const organisationIdNum = Number(organisationId)

  const mutation = useMutation({
    mutationFn: async (values: Parameters<typeof createOrganisationUser>[1]) =>
      createOrganisationUser(organisationIdNum, values),
    onSuccess: (response) => {
      clearServerErrors()
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationUsers.all(organisationIdNum),
      })
      // A new system manager is an organisation member, so Wallets lists them too.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.walletUsers.all(organisationIdNum),
      })
      toast.success(systemManagerAddedMessage(response))
      handleClose()
    },
    onError: handleMutationError,
  })

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-lg">
        <UserForm
          title="Add system manager"
          submitLabel="Add system manager"
          onSubmit={(values) => mutation.mutate(values)}
          isPending={mutation.isPending}
          serverErrors={serverErrors}
        />
      </SheetContent>
    </Sheet>
  )
}
